import _ from 'lodash';
import memoize from 'memoize-one';
import {type PlotMargin} from 'src/util/plotHelpers/plotHelpers';
import uPlot from 'uplot';

import {
  type PlotFontSize,
  plotFontSizeToPx,
} from '../../../util/plotHelpers/plotFontSize';
import {textWidth} from '../../../util/text';

/** length of tick in CSS pixels */
export function getuPlotTickSizePx(): number {
  return 5;
}

/** gap between tick values and axis baseline (or ticks, if enabled) in CSS pixels */
export function getuPlotTickGapPx(): number {
  return 5;
}

export function getuPlotTickFontSizePx(fontSize: PlotFontSize): number {
  return plotFontSizeToPx.axis[fontSize];
}

export function getuPlotLabelFontSizePx(fontSize: PlotFontSize): number {
  return plotFontSizeToPx.axis[fontSize];
}

/** font-size multiplier for multi-line axis values (similar to CSS line-height: 1.5em) */
export function getuPlotLineGap(): number {
  return 1;
}

const MIN_Y_AXIS_LABEL_WIDTH_PX = 10;

function getuPlotYAxisTickValuesSizePxImpl(
  fontSize: PlotFontSize,
  values: string[]
) {
  const tickGap = getuPlotTickGapPx();
  const tickSize = getuPlotTickSizePx();
  // y axis ticks are horizontal so we need to measure the width of the text
  let maxValueWidth = null;
  if (values.length > 0) {
    // single pass through the values to find the one with the max text width
    for (const value of values) {
      const width = textWidth(value, fontSize);
      if (width > (maxValueWidth ?? 0)) {
        maxValueWidth = width;
      }
    }
  }
  // (tick label + tick gap) + tick
  return (maxValueWidth ?? MIN_Y_AXIS_LABEL_WIDTH_PX) + tickGap + tickSize;
}

// memoize the function to avoid costly text width measurements for the same font size and values
const memoizedGetuPlotYAxisTickValuesSizePx = memoize(
  getuPlotYAxisTickValuesSizePxImpl,
  _.isEqual // custom equality function for values array equality check
);

/** width of y axis in CSS pixels alloted for tick values, tick gap & ticks, but excluding axis label or axis gap */
export function getuPlotYAxisTickValuesSizePx(
  fontSize: PlotFontSize
): uPlot.Axis.Size {
  const tickGap = getuPlotTickGapPx();
  const tickSize = getuPlotTickSizePx();
  return (_self: uPlot, values: string[] | null) => {
    if (values == null || values.length === 0) {
      return MIN_Y_AXIS_LABEL_WIDTH_PX + tickGap + tickSize;
    }
    return memoizedGetuPlotYAxisTickValuesSizePx(fontSize, values);
  };
}

/** height of x axis in CSS pixels alloted for tick values, tick gap & ticks, but excluding axis label or axis gap */
export function getuPlotXAxisTickValuesSizePx(
  fontSize: PlotFontSize
): uPlot.Axis.Size {
  const tickGap = getuPlotTickGapPx();
  const tickSize = getuPlotTickSizePx();
  const tickLabelHeight = getuPlotTickFontSizePx(fontSize) * getuPlotLineGap();
  return tickGap + tickSize + tickLabelHeight;
}

/**
 * This enables us to allow uPlot to automatically set the margins, but also align the crosshair overlay on top
 */
export function getuPlotMarginOverride(
  width: number,
  height: number,
  bbox: uPlot.BBox
): PlotMargin | null {
  const {top, left, width: canvasWidth, height: canvasHeight} = bbox;

  const topPx = top / uPlot.pxRatio;
  const leftPx = left / uPlot.pxRatio;
  const canvasWidthPx = canvasWidth / uPlot.pxRatio;
  const canvasHeightPx = canvasHeight / uPlot.pxRatio;
  const margin = {
    top: topPx,
    left: leftPx,
    // total width of the panel - the width of the plotting canvas - the left margin = the right margin
    right: width - canvasWidthPx - leftPx,
    // ditto for the bottom margin
    bottom: height - canvasHeightPx - topPx,
  };
  return margin;
}
